import * as THREE from "three";

/**
 * Environmental hazards for DUNGEON floors — spike traps (rising through a grate),
 * toxic grates (poison gas), and lava grates (molten pool under an iron grate).
 * Fully self-contained and procedural (no models/manifest): builds its own meshes
 * into the level group, places them on open floor (seeded, so a resume reproduces
 * them), and damages BOTH the player AND enemies standing on them — so you can
 * bait foes onto a trap (kills route through the normal kill path → loot + XP).
 *
 * ROLLBACK: flip GAME.dungeonHazards=false (constants.js) to disable, or delete
 * this file + the three `_hazards` lines in Game.js. Nothing else depends on it;
 * no save schema, no Player changes (damage is applied here, honouring the same
 * roll i-frames the rest of the game uses, and ignoring armour — it's the floor).
 */

let _smokeTex = null;
function smokeTex() {
  if (_smokeTex) return _smokeTex;
  const c = document.createElement("canvas"); c.width = c.height = 32;
  const g = c.getContext("2d");
  const gr = g.createRadialGradient(16, 16, 0, 16, 16, 16);
  gr.addColorStop(0, "rgba(255,255,255,1)"); gr.addColorStop(1, "rgba(255,255,255,0)");
  g.fillStyle = gr; g.fillRect(0, 0, 32, 32);
  _smokeTex = new THREE.CanvasTexture(c);
  return _smokeTex;
}

function disposeObj(o) {
  o.traverse((n) => {
    if (n.geometry) n.geometry.dispose();
    if (n.material) { (Array.isArray(n.material) ? n.material : [n.material]).forEach((m) => m.dispose()); }
  });
}

const SPIKE_CYCLE = 3.0;                 // full trap cycle (s)
// Spike height 0..1 over the cycle: a long SAFE window (spikes down), then a
// readable rise → fully up (deadly) → retract. The rising spikes ARE the tell,
// so you time your crossing — no colour flash needed.
function spikeHeight(p) {
  if (p < 1.8) return 0;                        // down — safe to cross
  if (p < 2.3) return (p - 1.8) / 0.5;          // rising — your cue to be clear
  if (p < 2.65) return 1;                       // fully up — deadly
  return Math.max(0, 1 - (p - 2.65) / 0.35);    // retracting
}

// Procedural molten-lava texture (dark cooled crust with glowing cracks); it
// scrolls each frame for a flowing look. Shared across pools; built once.
let _lavaTex = null;
function lavaTex() {
  if (_lavaTex) return _lavaTex;
  const c = document.createElement("canvas"); c.width = c.height = 128;
  const g = c.getContext("2d");
  g.fillStyle = "#ff8a1e"; g.fillRect(0, 0, 128, 128);
  for (let i = 0; i < 12; i++) {
    const x = Math.random() * 128, y = Math.random() * 128, r = 8 + Math.random() * 22;
    const gr = g.createRadialGradient(x, y, 0, x, y, r);
    gr.addColorStop(0, "#ffe27a"); gr.addColorStop(1, "rgba(255,138,30,0)");
    g.fillStyle = gr; g.fillRect(0, 0, 128, 128);
  }
  for (let i = 0; i < 80; i++) { const x = Math.random() * 128, y = Math.random() * 128, r = 4 + Math.random() * 11; g.fillStyle = "rgba(30,12,5,0.92)"; g.beginPath(); g.arc(x, y, r, 0, 7); g.fill(); }
  g.globalCompositeOperation = "lighter";
  for (let i = 0; i < 50; i++) { const x = Math.random() * 128, y = Math.random() * 128; g.fillStyle = "rgba(255,120,20,0.5)"; g.beginPath(); g.arc(x, y, 2, 0, 7); g.fill(); }
  g.globalCompositeOperation = "source-over";
  const t = new THREE.CanvasTexture(c); t.wrapS = t.wrapT = THREE.RepeatWrapping;
  _lavaTex = t; return t;
}

export class Hazards {
  constructor(group, registry, level, floor) {
    this.group = group;
    this.hazards = [];
    this._build(level, floor || 1);
  }

  // open floor cells, kept clear of the entrance and exit
  _cells(level) {
    const out = [], ps = level.playerStart, ex = level.exit;
    for (let y = 0; y < level.gridH; y++)
      for (let x = 0; x < level.gridW; x++) {
        if (!level.walkableGrid(x, y)) continue;
        if (Math.hypot(x - ps.gx, y - ps.gy) < 4) continue;   // never right at spawn
        if (Math.hypot(x - ex.gx, y - ex.gy) < 3) continue;   // nor blocking the exit
        out.push({ gx: x, gy: y });
      }
    return out;
  }

  _build(level, floor) {
    const cells = this._cells(level);
    if (!cells.length) return;
    for (let i = cells.length - 1; i > 0; i--) { const j = (Math.random() * (i + 1)) | 0; [cells[i], cells[j]] = [cells[j], cells[i]]; }
    const want = Math.min(cells.length, Math.min(11, 3 + Math.floor(floor)));
    const kinds = ["spike", "grate", "lava"];
    for (const c of cells) {
      if (this.hazards.length >= want) break;
      const w = level.gridToWorld(c.gx + 0.5, c.gy + 0.5);
      if (this.hazards.some((h) => Math.hypot(h.x - w.x, h.z - w.z) < 4.5)) continue;   // spacing
      const y = level.groundY ? level.groundY(w.x, w.z) : 0;
      const kind = kinds[(Math.random() * kinds.length) | 0];
      this.hazards.push(this[`_${kind}`](w.x, y, w.z, floor));
    }
  }

  // ---------- builders ----------
  _spike(x, y, z, floor) {
    const grp = new THREE.Group(); grp.position.set(x, y, z);
    // a metal grate the spikes rise through (no colour flash — the rising spikes tell you)
    const base = new THREE.Mesh(new THREE.BoxGeometry(1.7, 0.08, 1.7),
      new THREE.MeshStandardMaterial({ color: 0x1c2016, roughness: 0.95 }));
    base.position.y = 0.04; grp.add(base);
    const barMat = new THREE.MeshStandardMaterial({ color: 0x121509, metalness: 0.3, roughness: 0.7 });
    for (let i = -2; i <= 2; i++) {
      const bx = new THREE.Mesh(new THREE.BoxGeometry(0.09, 0.13, 1.6), barMat); bx.position.set(i * 0.34, 0.1, 0); grp.add(bx);
      const bz = new THREE.Mesh(new THREE.BoxGeometry(1.6, 0.13, 0.09), barMat); bz.position.set(0, 0.1, i * 0.34); grp.add(bz);
    }
    const spikes = new THREE.Group();
    const geo = new THREE.ConeGeometry(0.09, 0.8, 6);
    const mat = new THREE.MeshStandardMaterial({ color: 0xcac2b2, roughness: 0.45, metalness: 0.4 });
    for (let i = 0; i < 16; i++) { const s = new THREE.Mesh(geo, mat); s.position.set((i % 4 - 1.5) * 0.42, 0.4, ((i / 4 | 0) - 1.5) * 0.42); spikes.add(s); }
    spikes.position.y = -0.9; grp.add(spikes);   // start retracted below the grate
    this.group.add(grp);
    return { kind: "spike", x, z, r: 1.05, grp, spikes, phase: Math.random() * SPIKE_CYCLE, dmg: 12 + floor * 2, hit: false, etick: 0 };
  }

  _grate(x, y, z, floor) {
    const grp = new THREE.Group(); grp.position.set(x, y, z);
    const plate = new THREE.Mesh(new THREE.BoxGeometry(1.7, 0.1, 1.7),
      new THREE.MeshStandardMaterial({ color: 0x28311f, roughness: 0.9, emissive: 0x2c5a1e, emissiveIntensity: 0.3 }));
    plate.position.y = 0.05; grp.add(plate);
    const barMat = new THREE.MeshStandardMaterial({ color: 0x171a12 });
    for (let i = -2; i <= 2; i++) { const b = new THREE.Mesh(new THREE.BoxGeometry(1.6, 0.14, 0.09), barMat); b.position.set(0, 0.12, i * 0.34); grp.add(b); }
    const N = 16, pos = new Float32Array(N * 3), spd = new Float32Array(N);
    for (let i = 0; i < N; i++) { pos[i * 3] = (Math.random() - 0.5) * 1.4; pos[i * 3 + 1] = Math.random() * 1.6; pos[i * 3 + 2] = (Math.random() - 0.5) * 1.4; spd[i] = 0.4 + Math.random() * 0.5; }
    const bg = new THREE.BufferGeometry(); bg.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    const fumes = new THREE.Points(bg, new THREE.PointsMaterial({ color: 0x93e06a, size: 0.4, map: smokeTex(), transparent: true, opacity: 0.5, depthWrite: false, blending: THREE.AdditiveBlending, sizeAttenuation: true }));
    grp.add(fumes);
    this.group.add(grp);
    return { kind: "grate", x, z, r: 1.15, grp, fumes, spd, tick: 0, etick: 0, dmg: 3 + floor * 0.6 };
  }

  _lava(x, y, z, floor) {
    const grp = new THREE.Group(); grp.position.set(x, y, z);
    // molten pool sunk beneath an iron grate (same read as the toxic grate)
    const pool = new THREE.Mesh(new THREE.PlaneGeometry(1.6, 1.6), new THREE.MeshBasicMaterial({ map: lavaTex() }));
    pool.rotation.x = -Math.PI / 2; pool.position.y = 0.02; grp.add(pool);
    const barMat = new THREE.MeshStandardMaterial({ color: 0x140f0a, metalness: 0.45, roughness: 0.55 });
    for (let i = -2; i <= 2; i++) {
      const bx = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.14, 1.6), barMat); bx.position.set(i * 0.34, 0.12, 0); grp.add(bx);
      const bz = new THREE.Mesh(new THREE.BoxGeometry(1.6, 0.14, 0.1), barMat); bz.position.set(0, 0.12, i * 0.34); grp.add(bz);
    }
    const N = 12, pos = new Float32Array(N * 3), spd = new Float32Array(N);
    for (let i = 0; i < N; i++) { pos[i * 3] = (Math.random() - 0.5) * 1.4; pos[i * 3 + 1] = Math.random() * 1.2; pos[i * 3 + 2] = (Math.random() - 0.5) * 1.4; spd[i] = 0.5 + Math.random() * 0.6; }
    const bg = new THREE.BufferGeometry(); bg.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    const embers = new THREE.Points(bg, new THREE.PointsMaterial({ color: 0xffb24a, size: 0.18, map: smokeTex(), transparent: true, opacity: 0.85, depthWrite: false, blending: THREE.AdditiveBlending, sizeAttenuation: true }));
    grp.add(embers);
    this.group.add(grp);
    return { kind: "lava", x, z, r: 1.1, grp, embers, spd, tick: 0, etick: 0, dmg: 4 + floor };
  }

  // ---------- per-frame ----------
  update(dt, player, effects, combat) {
    if (_lavaTex) { _lavaTex.offset.x = (_lavaTex.offset.x + dt * 0.04) % 1; _lavaTex.offset.y = (_lavaTex.offset.y - dt * 0.025 + 1) % 1; }
    const px = player.pos.x, pz = player.pos.z;
    for (const h of this.hazards) {
      const pInside = Math.hypot(px - h.x, pz - h.z) < h.r;
      let dead = false;
      if (h.kind === "spike") dead = this._updSpike(h, dt, player, effects, combat, pInside);
      else if (h.kind === "grate") dead = this._updGrate(h, dt, player, effects, combat, pInside);
      else dead = this._updLava(h, dt, player, effects, combat, pInside);
      if (dead) return true;
    }
    return false;
  }

  /** Damage every enemy standing in the hazard (kills route through the normal
   *  kill path → loot + XP, so luring foes onto a trap rewards the player). */
  _hurtEnemies(h, dt, combat, dmg, color) {
    if (!combat || !combat.enemies) return;
    h.etick -= dt;
    if (h.etick > 0) return;
    h.etick = 0.35;
    for (const e of combat.enemies) {
      if (e.dead) continue;
      if (Math.hypot(e.pos.x - h.x, e.pos.z - h.z) < h.r) combat.petDamage(e, Math.max(1, Math.round(dmg)), color);
    }
  }

  _updSpike(h, dt, player, effects, combat, pInside) {
    h.phase = (h.phase + dt) % SPIKE_CYCLE;
    const height = spikeHeight(h.phase);          // 0..1, drives the visible rise
    h.spikes.position.y = -0.9 + height * 0.9;
    const deadly = height > 0.72;
    let dead = false;
    if (deadly) { if (!h.hit && pInside) { h.hit = true; dead = this._hurt(player, h.dmg, effects, "#ffd27a"); } }
    else if (height < 0.05) h.hit = false;        // re-arm once fully retracted
    if (deadly) this._hurtEnemies(h, dt, combat, h.dmg * 0.6, "#ffd27a");   // impale foes caught on it
    else h.etick = 0;
    return dead;
  }

  _updGrate(h, dt, player, effects, combat, pInside) {
    const arr = h.fumes.geometry.attributes.position.array;
    for (let i = 0; i < h.spd.length; i++) {
      arr[i * 3 + 1] += h.spd[i] * dt;
      if (arr[i * 3 + 1] > 1.7) { arr[i * 3 + 1] = 0; arr[i * 3] = (Math.random() - 0.5) * 1.4; arr[i * 3 + 2] = (Math.random() - 0.5) * 1.4; }
    }
    h.fumes.geometry.attributes.position.needsUpdate = true;
    let dead = false;
    if (pInside) { h.tick -= dt; if (h.tick <= 0) { h.tick = 0.5; dead = this._hurt(player, h.dmg, effects, "#93e06a"); } }
    else h.tick = 0;
    this._hurtEnemies(h, dt, combat, h.dmg, "#93e06a");   // foes in the fumes take it too
    return dead;
  }

  _updLava(h, dt, player, effects, combat, pInside) {
    const arr = h.embers.geometry.attributes.position.array;
    for (let i = 0; i < h.spd.length; i++) {
      arr[i * 3 + 1] += h.spd[i] * dt;
      if (arr[i * 3 + 1] > 1.4) { arr[i * 3 + 1] = 0; arr[i * 3] = (Math.random() - 0.5) * 1.4; arr[i * 3 + 2] = (Math.random() - 0.5) * 1.4; }
    }
    h.embers.geometry.attributes.position.needsUpdate = true;
    let dead = false;
    if (pInside) { h.tick -= dt; if (h.tick <= 0) { h.tick = 0.3; dead = this._hurt(player, h.dmg, effects, "#ff7a1e"); } }
    else h.tick = 0;
    this._hurtEnemies(h, dt, combat, h.dmg, "#ff7a1e");
    return dead;
  }

  /** Environmental damage: dodge-roll i-frames still save you; armour does not. */
  _hurt(player, dmg, effects, color) {
    if ((player.invuln || 0) > 0) return false;
    if (player.rollTimer > 0) { if (effects) effects.text(player.pos, "dodge", "#9fd0ff"); return false; }
    const taken = Math.max(1, Math.round(dmg));
    player.hp -= taken; player.hurt = Math.max(player.hurt || 0, 0.25);
    if (effects) effects.text(player.pos, `-${taken}`, color || "#ff8a3d");
    if (player.hp < 0) player.hp = 0;
    return player.hp <= 0;
  }

  dispose() {
    for (const h of this.hazards) { if (h.grp) { this.group.remove(h.grp); disposeObj(h.grp); } }
    this.hazards = [];
  }
}
